import { is } from '@client/helpers/dom.guards';
import { mergeSx, resolveMuiSlotProps } from '@client/helpers/mui.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getInputTextWidth } from '@client/helpers/dom.helpers';
import {
  OutlinedInputProps,
  TextField,
  TextFieldProps,
  TextFieldVariants,
} from '@mui/material';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import { toNumber } from '@shared/helpers/math.helpers';
import { omit } from '@shared/helpers/object.helpers';
import React, {
  FocusEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsMounted, useTimeout } from 'usehooks-ts';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_WIDTH = '100%';
/** Floor for {@link NodonTextFieldProps.autoWidth} so a short value still gets a usable field. */
export const MIN_AUTO_WIDTH = 80;
/** Added to the measured text width when sizing an {@link NodonTextFieldProps.autoWidth} field. */
export const INPUT_HORIZONTAL_PADDING = 16;
const ADORNMENT_SELECTOR = '.MuiInputAdornment-root';
const INPUT_BASE_ROOT_SELECTOR = '.MuiInputBase-root';

/**
 * Calculates the combined width of all adornments within the input root.
 * @param inputElement MUI input element whose adornments should be measured
 */
const getAdornmentWidths = (inputElement: HTMLInputElement): number => {
  const inputRoot = inputElement.closest(INPUT_BASE_ROOT_SELECTOR);
  if (!inputRoot) return 0;

  let totalWidth = 0;
  inputRoot.querySelectorAll(ADORNMENT_SELECTOR).forEach((node) => {
    if (!is.HTMLElement(node)) return;
    const computedStyle = window.getComputedStyle(node);
    const marginLeft = toNumber(computedStyle.marginLeft, 0);
    const marginRight = toNumber(computedStyle.marginRight, 0);
    totalWidth += node.getBoundingClientRect().width + marginLeft + marginRight;
  });

  return totalWidth;
};

/**
 * Gets the padding-right applied to the surrounding MUI InputBase container.
 * @param inputElement MUI input whose parent padding should be considered
 */
const getInputBasePaddingRight = (inputElement: HTMLInputElement): number => {
  const inputRoot = inputElement.closest(INPUT_BASE_ROOT_SELECTOR);
  if (!inputRoot) return 0;

  const { paddingRight } = window.getComputedStyle(inputRoot);
  return toNumber(paddingRight, 0);
};

export type NodonTextFieldProps<
  T extends TextFieldVariants = TextFieldVariants,
> = TextFieldProps<T> & {
  autoWidth?: boolean;
  autoWidthMin?: number;
  autoWidthPadding?: number;
  bold?: boolean;
  /** Maps to {@link TextFieldProps.slotProps} `input` for MUI v9. */
  InputProps?: OutlinedInputProps;
  /** Maps to {@link TextFieldProps.slotProps} `htmlInput` for MUI v9. */
  inputProps?: InputBaseComponentProps;
};

const NodonTextField: React.FC<NodonTextFieldProps> = (textFieldProps) => {
  const {
    onBlur,
    onFocus,
    InputProps,
    inputProps: htmlInputFromProps,
    autoWidth,
    autoWidthMin = MIN_AUTO_WIDTH,
    autoWidthPadding = INPUT_HORIZONTAL_PADDING,
    name,
    slotProps,
  } = textFieldProps;
  const inputName = useMemo(() => name ?? uuidv4(), [name]);
  const nestedHtmlInput = InputProps?.inputProps;
  const htmlInputProps = htmlInputFromProps ?? nestedHtmlInput;
  const ref = useRef<HTMLInputElement>(null);
  const isMounted = useIsMounted();
  const [autoWidthDelay, setAutoWidthDelay] = useState<number | null>(null);
  const [width, setWidth] = useState<string>('auto');

  // The internal ref drives measuring and selection, so it must always be populated.
  // Fork into the caller's ref too — it may be a callback (react-hook-form's `field.ref`).
  const callerInputRef = textFieldProps.inputRef;
  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      ref.current = node;
      if (guards.is.reactRefCallback(callerInputRef)) callerInputRef(node);
      else if (guards.is.reactRefObject(callerInputRef)) {
        callerInputRef.current = node;
      }
    },
    [callerInputRef],
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      onFocus?.(e);
    },
    [onFocus],
  );

  const updateAutoWidth = useCallback(() => {
    const inputElement = ref.current;
    if (!autoWidth || !inputElement) {
      setWidth(DEFAULT_WIDTH);
      return;
    }

    const adornmentWidth = getAdornmentWidths(inputElement);
    const contentWidth = getInputTextWidth(inputElement);
    const paddingRight = getInputBasePaddingRight(inputElement);

    // Keep Henrik's ellipsis away by accounting for adornments and padding
    const nextWidth = Math.max(
      autoWidthMin,
      contentWidth + autoWidthPadding + paddingRight + adornmentWidth,
    );
    setWidth(`${nextWidth}px`);
  }, [autoWidth, autoWidthMin, autoWidthPadding]);

  // `null` clears the timer; after it fires we go back to null so the next blur can start it again.
  useTimeout(() => {
    // Blur defers this; skip if the field already unmounted (jsdom is gone in tests).
    if (!isMounted()) return;
    updateAutoWidth();
    setAutoWidthDelay(null);
  }, autoWidthDelay);

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      onBlur?.(e);
      setAutoWidthDelay(100);
    },
    [onBlur],
  );

  useLayoutEffect(() => {
    updateAutoWidth();
  }, [
    updateAutoWidth,
    textFieldProps.value,
    textFieldProps.placeholder,
    InputProps,
    htmlInputProps,
  ]);

  useTimeout(
    () => {
      ref.current?.select();
    },
    textFieldProps.autoFocus ? 0 : null,
  );

  // Never add an htmlInput onKeyDown of our own: MUI InputBase spreads htmlInput
  // props onto the <input> after its own onKeyDown, so any handler here replaces
  // the one a caller passes via slotProps.input / InputProps (DEV-3897).
  const mergedHtmlInput = useMemo(
    () => ({
      ...htmlInputProps,
      style: {
        fontWeight: textFieldProps.bold ? 700 : undefined,
        ...htmlInputProps?.style,
      },
    }),
    [htmlInputProps, textFieldProps.bold],
  );

  return (
    <TextField
      autoComplete="off"
      {...omit(
        textFieldProps,
        'autoWidth',
        'autoWidthMin',
        'autoWidthPadding',
        'bold',
        'InputProps',
        'inputProps',
        'slotProps',
        'sx',
      )}
      name={inputName}
      sx={mergeSx(textFieldProps.sx, { width })}
      inputRef={setInputRef}
      onFocus={handleFocus}
      onBlur={handleBlur}
      slotProps={{
        ...slotProps,
        // Slot values may be ownerState callbacks; resolve before spreading.
        input: (ownerState) => ({
          ...(InputProps ? omit(InputProps, 'inputProps') : {}),
          ...resolveMuiSlotProps(slotProps?.input, ownerState),
        }),
        htmlInput: (ownerState) => ({
          ...mergedHtmlInput,
          ...resolveMuiSlotProps(slotProps?.htmlInput, ownerState),
        }),
      }}
    />
  );
};

export default NodonTextField;
